import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import canUseDom from '@nebula/can-use-dom';
import { ThemeContext } from '@nebula/core';
import loadThemePolyfillIfNeeded, { SUPPORT_CSS_VARS } from './polyfill';
import { mergeTheme } from './themes';
import { generateCSSVars, generateRootStyles } from './utils';
export var ThemeOverrider = function ThemeOverrider(_ref) {
  var className = _ref.className,
    theme = _ref.theme,
    children = _ref.children;
  var _useContext = useContext(ThemeContext),
    cssVars = _useContext.cssVars,
    setStack = _useContext.setStack;
  var newContextValue = useMemo(function () {
    return {
      cssVars: Object.assign({}, cssVars, generateCSSVars(theme)),
      setStack: setStack
    };
  }, [cssVars, setStack, theme]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: newContextValue
  }, /*#__PURE__*/React.createElement("div", {
    className: className,
    style: newContextValue.cssVars
  }, children));
};
export var ThemeProvider = function ThemeProvider(_ref2) {
  var theme = _ref2.theme,
    language = _ref2.language,
    loadPolyfill = _ref2.loadPolyfill,
    children = _ref2.children;
  var isTallText = language === 'th' || language === 'vi';
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    themeStack = _useState2[0],
    setThemeStack = _useState2[1];
  var _useState3 = useState(theme),
    _useState4 = _slicedToArray(_useState3, 2),
    mergedTheme = _useState4[0],
    setMergedTheme = _useState4[1];
  var _useState5 = useState(!!loadPolyfill),
    _useState6 = _slicedToArray(_useState5, 1),
    initialLoadPolyfill = _useState6[0];
  useEffect(function () {
    var mergedTheme = themeStack.reduce(function (acc, val) {
      return mergeTheme(acc, val);
    }, theme);
    setMergedTheme(mergedTheme);
  }, [theme, themeStack]);
  useLayoutEffect(function () {
    if (!initialLoadPolyfill || !canUseDom()) return;
    loadThemePolyfillIfNeeded();
  }, [initialLoadPolyfill]);
  var _useMemo = useMemo(function () {
      return generateRootStyles(mergedTheme, isTallText);
    }, [mergedTheme, isTallText]),
    _useMemo2 = _slicedToArray(_useMemo, 2),
    cssVars = _useMemo2[0],
    customStyle = _useMemo2[1];
  var themeContextValue = useMemo(function () {
    return {
      cssVars: cssVars,
      setStack: setThemeStack
    };
  }, [cssVars, setThemeStack]);
  var primaryColor = mergedTheme.color.primary;
  var isShopeeMall = primaryColor === '#d0011b';
  useEffect(function () {
    if (!canUseDom()) return;
    var container = window.document.body;
    var themeClassName = 'nt-' + (isShopeeMall ? 'm' : 's');
    var lineHeightClassName = 'nl-' + (isTallText ? 't' : 'l');
    container.classList.add(themeClassName);
    container.classList.add(lineHeightClassName);
    return function () {
      container.classList.remove(themeClassName);
      container.classList.remove(lineHeightClassName);
    };
  }, [isShopeeMall, isTallText]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: themeContextValue
  }, /*#__PURE__*/React.createElement("style", _extends({
    id: "nebula-style",
    dangerouslySetInnerHTML: {
      __html: customStyle
    }
  }, {})), children);
};
export var useRootThemeOverrider = function useRootThemeOverrider(partialTheme) {
  var _useContext2 = useContext(ThemeContext),
    setStack = _useContext2.setStack;
  useEffect(function () {
    var clonedTheme = _objectSpread({}, partialTheme);
    setStack(function (stack) {
      return [].concat(_toConsumableArray(stack), [clonedTheme]);
    });
    return function () {
      return setStack(function (stack) {
        return stack.filter(function (theme) {
          return theme !== clonedTheme;
        });
      });
    };
  }, [partialTheme]);
};
export var useCSSVar = function useCSSVar(cssVar) {
  if (SUPPORT_CSS_VARS || !cssVar) return cssVar;
  var _useContext3 = useContext(ThemeContext),
    cssVars = _useContext3.cssVars;
  return cssVar.replace(/var\(\s*(--n[0-9a-z-_]*),?.*?(?:[^)(]|\((?:[^)(]|\([^)(]*\))*\))*\)/g, function (original, cssVar) {
    return cssVars[cssVar] || original;
  });
};
export default ThemeProvider;